import './settings-leaf.css';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, Checkbox, LoadingBlock, Select, TextArea, TextInput } from '../ui.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import { formatPhoneForDisplay } from '@shared/phone.ts';
import { RING_TIMEOUT_MAX_SECONDS, RING_TIMEOUT_MIN_SECONDS } from '@shared/voice.ts';
import {
  AFTER_HOURS_OPTIONS,
  IVR_ACTIONS,
  IVR_DIGITS,
  ROUTING_MODES,
  TEAM_MAX_MEMBERS,
  TEAM_STRATEGIES,
  VOICEMAIL_NOTIFY_OPTIONS,
  type AccountNumber,
  type AfterHoursPolicy,
  type IvrAction,
  type IvrMenu,
  type RoutingMode,
  type SaveIvrMenuInput,
  type SaveVoiceTeamInput,
  type TeamStrategy,
  type UpdateVoiceNumberInput,
  type VoiceCallLogEntry,
  type VoiceLocationDefaults,
  type VoiceNumber,
  type VoiceOverrides,
  type VoiceRoutingView,
  type VoiceTeam,
  type VoicemailNotify,
} from '@shared/voice-routing.ts';

const RING_OPTIONS = Array.from(
  { length: (RING_TIMEOUT_MAX_SECONDS - RING_TIMEOUT_MIN_SECONDS) / 5 + 1 },
  (_, index) => RING_TIMEOUT_MIN_SECONDS + index * 5,
);
const QUERY_KEY = ['voice-routing'] as const;

/**
 * Settings › Calls and voicemail › Numbers, teams and locations: every
 * number the company takes calls on and what each one rings. A number rings
 * the forwarding phone from the Calls card, one employee, a named team (all
 * at once, one after another, or rotating who is tried first), or plays a
 * menu whose digits point at employees and teams. A location overrides the
 * company's defaults; a number overrides both.
 *
 * Below the Calls card, and only where it shows its form: the plan notice
 * there covers both.
 */
export default function VoiceRoutingCard() {
  const { t } = useTranslation();
  const { company, can } = useSession();
  const entitled = usePlanFeature('voice_inbox');
  if (!company?.voiceInboxAvailable || !can('company:write') || !entitled) return null;
  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="voice-routing-heading">
      <div className="work-section-heading">
        <h2 id="voice-routing-heading" className="mb-2 text-lg font-semibold">
          {t('voiceRouting.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('voiceRouting.description')}</p>
      <VoiceRoutingBody />
    </section>
  );
}

type Status = { tone: 'success' | 'error'; text: string } | null;

/** One outcome line and the field errors of the last save, per editor. */
function useOutcome() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  return {
    status,
    fieldErrors,
    clear: () => {
      setStatus(null);
      setFieldErrors({});
    },
    applied: (view: VoiceRoutingView, text: string) => {
      queryClient.setQueryData(QUERY_KEY, view);
      setFieldErrors({});
      setStatus({ tone: 'success', text });
    },
    failed: (caught: unknown) => {
      if (caught instanceof ApiRequestError) {
        setFieldErrors(caught.fields);
        setStatus({ tone: 'error', text: caught.message });
      } else setStatus({ tone: 'error', text: t('voiceRouting.saveError') });
    },
  };
}

function OutcomeLine({ status }: { status: Status }) {
  if (!status) return null;
  return (
    <div className="mb-3">
      <Alert tone={status.tone}>{status.text}</Alert>
    </div>
  );
}

function VoiceRoutingBody() {
  const { t } = useTranslation();
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: () => companyApi.voiceRouting() });
  if (query.isLoading) return <LoadingBlock label={t('voiceRouting.loading')} />;
  if (query.isError || !query.data)
    return (
      <Alert tone="error">
        {t('voiceRouting.loadError')}{' '}
        <Button variant="secondary" onClick={() => void query.refetch()}>
          {t('common.retry')}
        </Button>
      </Alert>
    );
  const view = query.data;
  return (
    <div className="space-y-6">
      {!view.available ? <Alert tone="info">{t('voiceSettings.notAvailable')}</Alert> : null}
      <NumbersSection view={view} />
      <TeamsSection view={view} />
      <MenusSection view={view} />
      <LocationsSection view={view} />
      <RecentCalls view={view} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

const INHERIT = '';

/** A select whose empty choice means "inherit", for the overrides a number or a location may set. */
function InheritSelect<T extends string>({
  label,
  hint,
  value,
  options,
  inheritLabel,
  onChange,
}: {
  label: string;
  hint?: string;
  value: T | null;
  options: ReadonlyArray<{ value: T; label: string }>;
  inheritLabel: string;
  onChange: (value: T | null) => void;
}) {
  return (
    <Select
      label={label}
      hint={hint}
      value={value ?? INHERIT}
      onChange={(event) => onChange(event.target.value === INHERIT ? null : (event.target.value as T))}
      options={[{ value: INHERIT, label: inheritLabel }, ...options]}
    />
  );
}

function OverridesFields({
  value,
  effective,
  onChange,
}: {
  value: VoiceOverrides;
  /** What inheriting gives right now, named in each inherit choice. */
  effective: { ringTimeoutSeconds: number; recordAnswered: boolean; voicemailNotify: string; afterHours: string };
  onChange: (value: VoiceOverrides) => void;
}) {
  const { t } = useTranslation();
  const set = <K extends keyof VoiceOverrides>(key: K, next: VoiceOverrides[K]) => onChange({ ...value, [key]: next });
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <InheritSelect
        label={t('voiceSettings.ringTimeout')}
        value={value.ringTimeoutSeconds === null ? null : String(value.ringTimeoutSeconds)}
        inheritLabel={t('voiceRouting.inherit', {
          value: t('voiceSettings.seconds', { seconds: effective.ringTimeoutSeconds }),
        })}
        options={RING_OPTIONS.map((seconds) => ({
          value: String(seconds),
          label: t('voiceSettings.seconds', { seconds }),
        }))}
        onChange={(next) => set('ringTimeoutSeconds', next === null ? null : Number(next))}
      />
      <InheritSelect
        label={t('voiceSettings.recordAnswered')}
        value={value.recordAnswered === null ? null : value.recordAnswered ? 'on' : 'off'}
        inheritLabel={t('voiceRouting.inherit', {
          value: effective.recordAnswered ? t('voiceRouting.on') : t('voiceRouting.off'),
        })}
        options={[
          { value: 'on', label: t('voiceRouting.on') },
          { value: 'off', label: t('voiceRouting.off') },
        ]}
        onChange={(next) => set('recordAnswered', next === null ? null : next === 'on')}
      />
      <InheritSelect
        label={t('voiceSettings.voicemailNotify')}
        value={value.voicemailNotify}
        inheritLabel={t('voiceRouting.inherit', {
          value: t(`voiceRouting.voicemailNotify.${effective.voicemailNotify}`),
        })}
        options={VOICEMAIL_NOTIFY_OPTIONS.map((option) => ({
          value: option,
          label: t(`voiceRouting.voicemailNotify.${option}`),
        }))}
        onChange={(next) => set('voicemailNotify', next as VoicemailNotify | null)}
      />
      <InheritSelect
        label={t('voiceSettings.afterHours')}
        value={value.afterHours}
        inheritLabel={t('voiceRouting.inherit', { value: t(`voiceRouting.afterHours.${effective.afterHours}`) })}
        options={AFTER_HOURS_OPTIONS.map((option) => ({
          value: option,
          label: t(`voiceRouting.afterHours.${option}`),
        }))}
        onChange={(next) => set('afterHours', next as AfterHoursPolicy | null)}
      />
    </div>
  );
}

function EditorFrame({
  title,
  children,
  onSubmit,
  busy,
  disabled,
  onCancel,
  extra,
}: {
  title: string;
  children: ReactNode;
  onSubmit: () => void;
  busy: boolean;
  disabled: boolean;
  onCancel: () => void;
  extra?: ReactNode;
}) {
  const { t } = useTranslation();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };
  return (
    <form onSubmit={submit} noValidate className="rounded border border-[color:var(--color-border)] p-3">
      <h4 className="mb-2 font-semibold">{title}</h4>
      {children}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="submit" busy={busy} disabled={disabled}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        {extra}
      </div>
    </form>
  );
}

function nameOf(view: VoiceRoutingView, kind: 'employee' | 'team' | 'menu' | 'location', id: string | null): string {
  if (!id) return '';
  switch (kind) {
    case 'employee':
      return view.employees.find((employee) => employee.id === id)?.name ?? '';
    case 'team':
      return view.teams.find((team) => team.id === id)?.name ?? '';
    case 'menu':
      return view.ivrMenus.find((menu) => menu.id === id)?.name ?? '';
    case 'location':
      return view.locations.find((location) => location.locationId === id)?.name ?? '';
  }
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

function modeSummary(
  t: (key: string, options?: Record<string, unknown>) => string,
  view: VoiceRoutingView,
  number: VoiceNumber,
) {
  switch (number.mode) {
    case 'company':
      return t('voiceRouting.modeSummary.company');
    case 'direct':
      return t('voiceRouting.modeSummary.direct', { name: nameOf(view, 'employee', number.targetUserId) });
    case 'team': {
      const team = view.teams.find((candidate) => candidate.id === number.targetTeamId);
      return t('voiceRouting.modeSummary.team', {
        name: team?.name ?? '',
        strategy: t(`voiceRouting.strategy.${number.teamStrategy ?? team?.strategy ?? 'simulring'}`),
      });
    }
    case 'ivr':
      return t('voiceRouting.modeSummary.ivr', { name: nameOf(view, 'menu', number.ivrMenuId) });
  }
}

function NumbersSection({ view }: { view: VoiceRoutingView }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  return (
    <div>
      <h3 className="mb-1 font-semibold">{t('voiceRouting.numbers')}</h3>
      <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('voiceRouting.numbersHint')}</p>
      {view.numbers.length === 0 ? <p className="mb-2 text-sm">{t('voiceRouting.noNumbers')}</p> : null}
      <ul className="space-y-2">
        {view.numbers.map((number) => (
          <li key={number.id} data-testid="voice-number">
            {editing === number.id ? (
              <NumberEditor view={view} number={number} onDone={() => setEditing(null)} />
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-[color:var(--color-border)] p-3">
                <div className="text-sm">
                  <p className="font-semibold">
                    {number.label ? `${number.label} · ` : ''}
                    {formatPhoneForDisplay(number.e164)}
                    {number.kind === 'line' ? ` (${t('voiceRouting.textingLine')})` : ''}
                  </p>
                  <p>
                    {modeSummary(t, view, number)}
                    {number.locationId ? ` · ${nameOf(view, 'location', number.locationId)}` : ''}
                  </p>
                </div>
                <Button type="button" variant="secondary" onClick={() => setEditing(number.id)}>
                  {t('common.edit')}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-3">
        {adding ? (
          <AddNumber view={view} onDone={() => setAdding(false)} />
        ) : view.canAddNumbers ? (
          <Button type="button" variant="secondary" onClick={() => setAdding(true)} disabled={!view.available}>
            {t('voiceRouting.addNumber')}
          </Button>
        ) : (
          <p className="text-sm text-[color:var(--color-muted)]">{t('voiceRouting.addNumberOwnAccount')}</p>
        )}
      </div>
    </div>
  );
}

function AddNumber({ view, onDone }: { view: VoiceRoutingView; onDone: () => void }) {
  const { t } = useTranslation();
  const outcome = useOutcome();
  const [phone, setPhone] = useState('');
  const [label, setLabel] = useState('');
  const account = useQuery({ queryKey: ['voice-account-numbers'], queryFn: () => companyApi.voiceAccountNumbers() });
  const add = useMutation({
    mutationFn: (body: { phoneNumber: string; label?: string }) => companyApi.addVoiceNumber(body),
    onSuccess: (data) => {
      outcome.applied(data, t('voiceRouting.numberAdded'));
      void account.refetch();
      onDone();
    },
    onError: outcome.failed,
  });
  const numbers: AccountNumber[] = account.data?.numbers ?? [];
  return (
    <EditorFrame
      title={t('voiceRouting.addNumber')}
      busy={add.isPending}
      disabled={!view.available || !phone.trim()}
      onSubmit={() => add.mutate({ phoneNumber: phone.trim(), label: label.trim() || undefined })}
      onCancel={onDone}
    >
      <OutcomeLine status={outcome.status} />
      <p className="mb-2 text-sm">{t('voiceRouting.addNumberHint')}</p>
      {account.isLoading ? <LoadingBlock label={t('voiceRouting.loading')} /> : null}
      {account.isError ? <Alert tone="warning">{t('voiceRouting.accountNumbersError')}</Alert> : null}
      {numbers.length > 0 ? (
        <ul className="mb-2 space-y-1 text-sm">
          {numbers.map((candidate) => (
            <li key={candidate.e164} className="flex items-center justify-between gap-2">
              <span>
                {formatPhoneForDisplay(candidate.e164)}
                {candidate.friendlyName ? ` · ${candidate.friendlyName}` : ''}
                {!candidate.voice ? ` · ${t('voiceRouting.noVoice')}` : ''}
              </span>
              <Button
                type="button"
                variant="secondary"
                disabled={!candidate.voice || add.isPending}
                onClick={() => add.mutate({ phoneNumber: candidate.e164, label: candidate.friendlyName ?? undefined })}
              >
                {t('voiceRouting.add')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      <TextInput
        label={t('voiceRouting.phoneNumber')}
        type="tel"
        value={phone}
        error={outcome.fieldErrors.phoneNumber}
        onChange={(event) => setPhone(event.target.value)}
      />
      <TextInput label={t('voiceRouting.label')} value={label} onChange={(event) => setLabel(event.target.value)} />
    </EditorFrame>
  );
}

function NumberEditor({ view, number, onDone }: { view: VoiceRoutingView; number: VoiceNumber; onDone: () => void }) {
  const { t } = useTranslation();
  const outcome = useOutcome();
  const [draft, setDraft] = useState<UpdateVoiceNumberInput>({
    label: number.label,
    locationId: number.locationId,
    mode: number.mode,
    targetUserId: number.targetUserId,
    targetTeamId: number.targetTeamId,
    teamStrategy: number.teamStrategy,
    ivrMenuId: number.ivrMenuId,
    overrides: number.overrides,
  });
  const set = <K extends keyof UpdateVoiceNumberInput>(key: K, value: UpdateVoiceNumberInput[K]) =>
    setDraft({ ...draft, [key]: value });
  const save = useMutation({
    mutationFn: (body: UpdateVoiceNumberInput) => companyApi.updateVoiceNumber(number.id, body),
    onSuccess: (data) => {
      outcome.applied(data, t('voiceRouting.numberSaved'));
      onDone();
    },
    onError: outcome.failed,
  });
  const remove = useMutation({
    mutationFn: () => companyApi.removeVoiceNumber(number.id),
    onSuccess: (data) => {
      outcome.applied(data, t('voiceRouting.numberRemoved'));
      onDone();
    },
    onError: outcome.failed,
  });
  const location = view.locations.find((candidate) => candidate.locationId === draft.locationId);
  // What inheriting means for this number: the location's overrides over the company's defaults.
  const inherited = {
    ringTimeoutSeconds: location?.overrides.ringTimeoutSeconds ?? view.companyDefaults.ringTimeoutSeconds,
    recordAnswered: location?.overrides.recordAnswered ?? view.companyDefaults.recordAnswered,
    voicemailNotify: location?.overrides.voicemailNotify ?? view.companyDefaults.voicemailNotify,
    afterHours: location?.overrides.afterHours ?? view.companyDefaults.afterHours,
  };
  const employeeOptions = view.employees
    .filter((employee) => employee.active)
    .map((employee) => ({
      value: employee.id,
      label: employee.hasPhone ? employee.name : `${employee.name} (${t('voiceRouting.noPhone')})`,
    }));
  return (
    <EditorFrame
      title={`${number.label ? `${number.label} · ` : ''}${formatPhoneForDisplay(number.e164)}`}
      busy={save.isPending}
      disabled={!view.available}
      onSubmit={() => save.mutate(draft)}
      onCancel={onDone}
      extra={
        number.kind === 'added' ? (
          <Button type="button" variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
            {t('voiceRouting.removeNumber')}
          </Button>
        ) : null
      }
    >
      <OutcomeLine status={outcome.status} />
      <TextInput
        label={t('voiceRouting.label')}
        value={draft.label ?? ''}
        error={outcome.fieldErrors.label}
        onChange={(event) => set('label', event.target.value || null)}
      />
      <Select
        label={t('voiceRouting.location')}
        hint={t('voiceRouting.locationHint')}
        value={draft.locationId ?? INHERIT}
        error={outcome.fieldErrors.locationId}
        onChange={(event) => set('locationId', event.target.value || null)}
        options={[
          { value: INHERIT, label: t('voiceRouting.companyWide') },
          ...view.locations.map((candidate) => ({ value: candidate.locationId, label: candidate.name })),
        ]}
      />
      <fieldset className="mt-3">
        <legend className="text-sm font-semibold">{t('voiceRouting.mode')}</legend>
        {ROUTING_MODES.map((mode) => (
          <label key={mode} className="mt-1 flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={`mode-${number.id}`}
              checked={draft.mode === mode}
              onChange={() => set('mode', mode as RoutingMode)}
            />
            {t(`voiceRouting.modes.${mode}`)}
          </label>
        ))}
      </fieldset>
      {draft.mode === 'direct' ? (
        <Select
          label={t('voiceRouting.employee')}
          value={draft.targetUserId ?? INHERIT}
          error={outcome.fieldErrors.targetUserId}
          onChange={(event) => set('targetUserId', event.target.value || null)}
          options={[{ value: INHERIT, label: t('voiceRouting.choose') }, ...employeeOptions]}
        />
      ) : null}
      {draft.mode === 'team' ? (
        <>
          <Select
            label={t('voiceRouting.team')}
            value={draft.targetTeamId ?? INHERIT}
            error={outcome.fieldErrors.targetTeamId}
            onChange={(event) => set('targetTeamId', event.target.value || null)}
            options={[
              { value: INHERIT, label: t('voiceRouting.choose') },
              ...view.teams.map((team) => ({ value: team.id, label: team.name })),
            ]}
          />
          <InheritSelect
            label={t('voiceRouting.strategyOnNumber')}
            value={draft.teamStrategy ?? null}
            inheritLabel={t('voiceRouting.inherit', {
              value: t(
                `voiceRouting.strategy.${view.teams.find((team) => team.id === draft.targetTeamId)?.strategy ?? 'simulring'}`,
              ),
            })}
            options={TEAM_STRATEGIES.map((strategy) => ({
              value: strategy,
              label: t(`voiceRouting.strategy.${strategy}`),
            }))}
            onChange={(next) => set('teamStrategy', next as TeamStrategy | null)}
          />
        </>
      ) : null}
      {draft.mode === 'ivr' ? (
        <Select
          label={t('voiceRouting.menu')}
          value={draft.ivrMenuId ?? INHERIT}
          error={outcome.fieldErrors.ivrMenuId}
          onChange={(event) => set('ivrMenuId', event.target.value || null)}
          options={[
            { value: INHERIT, label: t('voiceRouting.choose') },
            ...view.ivrMenus.map((menu) => ({ value: menu.id, label: menu.name })),
          ]}
        />
      ) : null}
      <p className="mt-3 text-sm font-semibold">{t('voiceRouting.overrides')}</p>
      <p className="field-hint">{t('voiceRouting.overridesHint')}</p>
      <OverridesFields
        value={draft.overrides}
        effective={inherited}
        onChange={(overrides) => set('overrides', overrides)}
      />
    </EditorFrame>
  );
}

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------

interface TeamDraft {
  name: string;
  locationId: string | null;
  strategy: TeamStrategy;
  ringTimeoutSeconds: number | null;
  members: Array<{ userId: string; phoneOverride: string; enabled: boolean }>;
}

const NEW_TEAM: TeamDraft = {
  name: '',
  locationId: null,
  strategy: 'simulring',
  ringTimeoutSeconds: null,
  members: [],
};

function teamDraft(team: VoiceTeam): TeamDraft {
  return {
    name: team.name,
    locationId: team.locationId,
    strategy: team.strategy,
    ringTimeoutSeconds: team.ringTimeoutSeconds,
    members: team.members.map((member) => ({
      userId: member.userId,
      phoneOverride: member.phoneOverride ?? '',
      enabled: member.enabled,
    })),
  };
}

function teamBody(draft: TeamDraft): SaveVoiceTeamInput {
  return {
    name: draft.name.trim(),
    locationId: draft.locationId,
    strategy: draft.strategy,
    ringTimeoutSeconds: draft.ringTimeoutSeconds,
    members: draft.members.map((member) => ({
      userId: member.userId,
      phoneOverride: member.phoneOverride.trim() || null,
      enabled: member.enabled,
    })),
  };
}

function TeamsSection({ view }: { view: VoiceRoutingView }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  return (
    <div>
      <h3 className="mb-1 font-semibold">{t('voiceRouting.teams')}</h3>
      <p className="mb-2 text-sm text-[color:var(--color-muted)]">
        {t('voiceRouting.teamsHint', { max: TEAM_MAX_MEMBERS })}
      </p>
      <ul className="space-y-2">
        {view.teams.map((team) => (
          <li key={team.id} data-testid="voice-team">
            {editing === team.id ? (
              <TeamEditor view={view} team={team} onDone={() => setEditing(null)} />
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-[color:var(--color-border)] p-3">
                <div className="text-sm">
                  <p className="font-semibold">{team.name}</p>
                  <p>
                    {t(`voiceRouting.strategy.${team.strategy}`)} ·{' '}
                    {t('voiceRouting.memberCount', { count: team.members.length })}
                    {team.locationId ? ` · ${nameOf(view, 'location', team.locationId)}` : ''}
                  </p>
                </div>
                <Button type="button" variant="secondary" onClick={() => setEditing(team.id)}>
                  {t('common.edit')}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-3">
        {editing === 'new' ? (
          <TeamEditor view={view} team={null} onDone={() => setEditing(null)} />
        ) : (
          <Button type="button" variant="secondary" onClick={() => setEditing('new')} disabled={!view.available}>
            {t('voiceRouting.newTeam')}
          </Button>
        )}
      </div>
    </div>
  );
}

function TeamEditor({ view, team, onDone }: { view: VoiceRoutingView; team: VoiceTeam | null; onDone: () => void }) {
  const { t } = useTranslation();
  const outcome = useOutcome();
  const [draft, setDraft] = useState<TeamDraft>(team ? teamDraft(team) : NEW_TEAM);
  const [adding, setAdding] = useState('');
  const save = useMutation({
    mutationFn: (body: SaveVoiceTeamInput) =>
      team ? companyApi.updateVoiceTeam(team.id, body) : companyApi.createVoiceTeam(body),
    onSuccess: (data) => {
      outcome.applied(data, t('voiceRouting.teamSaved'));
      onDone();
    },
    onError: outcome.failed,
  });
  const remove = useMutation({
    mutationFn: () => companyApi.deleteVoiceTeam(team!.id),
    onSuccess: (data) => {
      outcome.applied(data, t('voiceRouting.teamDeleted'));
      onDone();
    },
    onError: outcome.failed,
  });
  const members = draft.members;
  const move = (index: number, delta: number) => {
    const next = [...members];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    setDraft({ ...draft, members: next });
  };
  const available = view.employees.filter(
    (employee) => employee.active && !members.some((member) => member.userId === employee.id),
  );
  return (
    <EditorFrame
      title={team ? team.name : t('voiceRouting.newTeam')}
      busy={save.isPending}
      disabled={!view.available || !draft.name.trim()}
      onSubmit={() => save.mutate(teamBody(draft))}
      onCancel={onDone}
      extra={
        team ? (
          <Button
            type="button"
            variant="danger"
            busy={remove.isPending}
            disabled={team.inUseBy > 0}
            title={team.inUseBy > 0 ? t('voiceRouting.teamInUse', { count: team.inUseBy }) : undefined}
            onClick={() => remove.mutate()}
          >
            {t('voiceRouting.deleteTeam')}
          </Button>
        ) : null
      }
    >
      <OutcomeLine status={outcome.status} />
      <TextInput
        label={t('voiceRouting.teamName')}
        value={draft.name}
        error={outcome.fieldErrors.name}
        onChange={(event) => setDraft({ ...draft, name: event.target.value })}
      />
      <Select
        label={t('voiceRouting.location')}
        hint={t('voiceRouting.teamLocationHint')}
        value={draft.locationId ?? INHERIT}
        error={outcome.fieldErrors.locationId}
        onChange={(event) => setDraft({ ...draft, locationId: event.target.value || null })}
        options={[
          { value: INHERIT, label: t('voiceRouting.companyWide') },
          ...view.locations.map((candidate) => ({ value: candidate.locationId, label: candidate.name })),
        ]}
      />
      <Select
        label={t('voiceRouting.strategyLabel')}
        hint={t('voiceRouting.strategyHint')}
        value={draft.strategy}
        onChange={(event) => setDraft({ ...draft, strategy: event.target.value as TeamStrategy })}
        options={TEAM_STRATEGIES.map((strategy) => ({
          value: strategy,
          label: t(`voiceRouting.strategy.${strategy}`),
        }))}
      />
      <InheritSelect
        label={t('voiceSettings.ringTimeout')}
        hint={t('voiceRouting.teamRingTimeoutHint')}
        value={draft.ringTimeoutSeconds === null ? null : String(draft.ringTimeoutSeconds)}
        inheritLabel={t('voiceRouting.inheritPlain')}
        options={RING_OPTIONS.map((seconds) => ({
          value: String(seconds),
          label: t('voiceSettings.seconds', { seconds }),
        }))}
        onChange={(next) => setDraft({ ...draft, ringTimeoutSeconds: next === null ? null : Number(next) })}
      />
      <p className="mt-3 text-sm font-semibold">{t('voiceRouting.members')}</p>
      {outcome.fieldErrors.members ? (
        <p className="field-error" role="alert">
          {outcome.fieldErrors.members}
        </p>
      ) : null}
      <ol className="space-y-2">
        {members.map((member, index) => {
          const employee = view.employees.find((candidate) => candidate.id === member.userId);
          return (
            <li key={member.userId} className="rounded border border-[color:var(--color-border)] p-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">
                  {index + 1}. {employee?.name ?? member.userId}
                </span>
                <Button type="button" variant="ghost" onClick={() => move(index, -1)} disabled={index === 0}>
                  {t('voiceRouting.moveUp')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => move(index, 1)}
                  disabled={index === members.length - 1}
                >
                  {t('voiceRouting.moveDown')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setDraft({ ...draft, members: members.filter((_, at) => at !== index) })}
                >
                  {t('common.remove')}
                </Button>
              </div>
              <div className="mt-1 grid gap-2 sm:grid-cols-2">
                <TextInput
                  label={t('voiceRouting.phoneOverride')}
                  hint={employee && !employee.hasPhone ? t('voiceRouting.noPhoneHint') : undefined}
                  type="tel"
                  value={member.phoneOverride}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      members: members.map((entry, at) =>
                        at === index ? { ...entry, phoneOverride: event.target.value } : entry,
                      ),
                    })
                  }
                />
                <Checkbox
                  label={t('voiceRouting.memberEnabled')}
                  checked={member.enabled}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      members: members.map((entry, at) =>
                        at === index ? { ...entry, enabled: event.target.checked } : entry,
                      ),
                    })
                  }
                />
              </div>
            </li>
          );
        })}
      </ol>
      {members.length < TEAM_MAX_MEMBERS ? (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <Select
            label={t('voiceRouting.addMember')}
            value={adding}
            onChange={(event) => setAdding(event.target.value)}
            options={[
              { value: INHERIT, label: t('voiceRouting.choose') },
              ...available.map((employee) => ({
                value: employee.id,
                label: employee.hasPhone ? employee.name : `${employee.name} (${t('voiceRouting.noPhone')})`,
              })),
            ]}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={!adding}
            onClick={() => {
              if (!adding) return;
              setDraft({ ...draft, members: [...members, { userId: adding, phoneOverride: '', enabled: true }] });
              setAdding('');
            }}
          >
            {t('voiceRouting.add')}
          </Button>
        </div>
      ) : (
        <p className="field-hint">{t('voiceRouting.teamFull', { max: TEAM_MAX_MEMBERS })}</p>
      )}
    </EditorFrame>
  );
}

// ---------------------------------------------------------------------------
// Menus
// ---------------------------------------------------------------------------

interface MenuDraft {
  name: string;
  promptText: string;
  options: Array<{
    digit: string;
    label: string;
    action: IvrAction;
    targetUserId: string | null;
    targetTeamId: string | null;
    submenuId: string | null;
  }>;
}

const NEW_MENU: MenuDraft = { name: '', promptText: '', options: [] };

function menuDraft(menu: IvrMenu): MenuDraft {
  return {
    name: menu.name,
    promptText: menu.promptText,
    options: menu.options.map((option) => ({
      digit: option.digit,
      label: option.label,
      action: option.action,
      targetUserId: option.targetUserId,
      targetTeamId: option.targetTeamId,
      submenuId: option.submenuId,
    })),
  };
}

function menuBody(draft: MenuDraft): SaveIvrMenuInput {
  return {
    name: draft.name.trim(),
    promptText: draft.promptText.trim(),
    options: draft.options.map((option) => ({
      digit: option.digit as SaveIvrMenuInput['options'][number]['digit'],
      label: option.label.trim(),
      action: option.action,
      targetUserId: option.action === 'user' ? option.targetUserId : null,
      targetTeamId: option.action === 'team' ? option.targetTeamId : null,
      submenuId: option.action === 'submenu' ? option.submenuId : null,
    })),
  };
}

function MenusSection({ view }: { view: VoiceRoutingView }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  return (
    <div>
      <h3 className="mb-1 font-semibold">{t('voiceRouting.menus')}</h3>
      <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('voiceRouting.menusHint')}</p>
      <ul className="space-y-2">
        {view.ivrMenus.map((menu) => (
          <li key={menu.id} data-testid="voice-menu">
            {editing === menu.id ? (
              <MenuEditor view={view} menu={menu} onDone={() => setEditing(null)} />
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-[color:var(--color-border)] p-3">
                <div className="text-sm">
                  <p className="font-semibold">{menu.name}</p>
                  <p>{t('voiceRouting.optionCount', { count: menu.options.length })}</p>
                </div>
                <Button type="button" variant="secondary" onClick={() => setEditing(menu.id)}>
                  {t('common.edit')}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-3">
        {editing === 'new' ? (
          <MenuEditor view={view} menu={null} onDone={() => setEditing(null)} />
        ) : (
          <Button type="button" variant="secondary" onClick={() => setEditing('new')} disabled={!view.available}>
            {t('voiceRouting.newMenu')}
          </Button>
        )}
      </div>
    </div>
  );
}

function MenuEditor({ view, menu, onDone }: { view: VoiceRoutingView; menu: IvrMenu | null; onDone: () => void }) {
  const { t } = useTranslation();
  const outcome = useOutcome();
  const [draft, setDraft] = useState<MenuDraft>(menu ? menuDraft(menu) : NEW_MENU);
  const save = useMutation({
    mutationFn: (body: SaveIvrMenuInput) =>
      menu ? companyApi.updateIvrMenu(menu.id, body) : companyApi.createIvrMenu(body),
    onSuccess: (data) => {
      outcome.applied(data, t('voiceRouting.menuSaved'));
      onDone();
    },
    onError: outcome.failed,
  });
  const remove = useMutation({
    mutationFn: () => companyApi.deleteIvrMenu(menu!.id),
    onSuccess: (data) => {
      outcome.applied(data, t('voiceRouting.menuDeleted'));
      onDone();
    },
    onError: outcome.failed,
  });
  const setOption = (index: number, patch: Partial<MenuDraft['options'][number]>) =>
    setDraft({
      ...draft,
      options: draft.options.map((option, at) => (at === index ? { ...option, ...patch } : option)),
    });
  const freeDigit = IVR_DIGITS.find((digit) => !draft.options.some((option) => option.digit === digit));
  return (
    <EditorFrame
      title={menu ? menu.name : t('voiceRouting.newMenu')}
      busy={save.isPending}
      disabled={!view.available || !draft.name.trim() || !draft.promptText.trim()}
      onSubmit={() => save.mutate(menuBody(draft))}
      onCancel={onDone}
      extra={
        menu ? (
          <Button
            type="button"
            variant="danger"
            busy={remove.isPending}
            disabled={menu.inUseBy > 0}
            title={menu.inUseBy > 0 ? t('voiceRouting.menuInUse', { count: menu.inUseBy }) : undefined}
            onClick={() => remove.mutate()}
          >
            {t('voiceRouting.deleteMenu')}
          </Button>
        ) : null
      }
    >
      <OutcomeLine status={outcome.status} />
      <TextInput
        label={t('voiceRouting.menuName')}
        value={draft.name}
        error={outcome.fieldErrors.name}
        onChange={(event) => setDraft({ ...draft, name: event.target.value })}
      />
      <TextArea
        label={t('voiceRouting.promptText')}
        hint={t('voiceRouting.promptTextHint')}
        rows={3}
        value={draft.promptText}
        error={outcome.fieldErrors.promptText}
        onChange={(event) => setDraft({ ...draft, promptText: event.target.value })}
      />
      <p className="mt-3 text-sm font-semibold">{t('voiceRouting.options')}</p>
      {outcome.fieldErrors.options ? (
        <p className="field-error" role="alert">
          {outcome.fieldErrors.options}
        </p>
      ) : null}
      <ul className="space-y-2">
        {draft.options.map((option, index) => (
          <li key={index} className="grid gap-2 rounded border border-[color:var(--color-border)] p-2 sm:grid-cols-4">
            <Select
              label={t('voiceRouting.digit')}
              value={option.digit}
              onChange={(event) => setOption(index, { digit: event.target.value })}
              options={IVR_DIGITS.map((digit) => ({
                value: digit,
                label: digit,
                disabled: draft.options.some((other, at) => at !== index && other.digit === digit),
              }))}
            />
            <TextInput
              label={t('voiceRouting.optionLabel')}
              value={option.label}
              onChange={(event) => setOption(index, { label: event.target.value })}
            />
            <Select
              label={t('voiceRouting.action')}
              value={option.action}
              onChange={(event) => setOption(index, { action: event.target.value as IvrAction })}
              options={IVR_ACTIONS.map((action) => ({ value: action, label: t(`voiceRouting.actions.${action}`) }))}
            />
            {option.action === 'user' ? (
              <Select
                label={t('voiceRouting.employee')}
                value={option.targetUserId ?? INHERIT}
                onChange={(event) => setOption(index, { targetUserId: event.target.value || null })}
                options={[
                  { value: INHERIT, label: t('voiceRouting.choose') },
                  ...view.employees
                    .filter((employee) => employee.active)
                    .map((employee) => ({ value: employee.id, label: employee.name })),
                ]}
              />
            ) : option.action === 'team' ? (
              <Select
                label={t('voiceRouting.team')}
                value={option.targetTeamId ?? INHERIT}
                onChange={(event) => setOption(index, { targetTeamId: event.target.value || null })}
                options={[
                  { value: INHERIT, label: t('voiceRouting.choose') },
                  ...view.teams.map((team) => ({ value: team.id, label: team.name })),
                ]}
              />
            ) : option.action === 'submenu' ? (
              <Select
                label={t('voiceRouting.menu')}
                value={option.submenuId ?? INHERIT}
                onChange={(event) => setOption(index, { submenuId: event.target.value || null })}
                options={[
                  { value: INHERIT, label: t('voiceRouting.choose') },
                  ...view.ivrMenus
                    .filter((candidate) => candidate.id !== menu?.id)
                    .map((candidate) => ({ value: candidate.id, label: candidate.name })),
                ]}
              />
            ) : (
              <div />
            )}
            <div className="sm:col-span-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDraft({ ...draft, options: draft.options.filter((_, at) => at !== index) })}
              >
                {t('common.remove')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
      {freeDigit ? (
        <div className="mt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              setDraft({
                ...draft,
                options: [
                  ...draft.options,
                  {
                    digit: freeDigit,
                    label: '',
                    action: 'voicemail',
                    targetUserId: null,
                    targetTeamId: null,
                    submenuId: null,
                  },
                ],
              })
            }
          >
            {t('voiceRouting.addOption')}
          </Button>
        </div>
      ) : null}
    </EditorFrame>
  );
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

function LocationsSection({ view }: { view: VoiceRoutingView }) {
  const { t } = useTranslation();
  if (view.locations.length <= 1) return null;
  return (
    <div>
      <h3 className="mb-1 font-semibold">{t('voiceRouting.locations')}</h3>
      <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('voiceRouting.locationsHint')}</p>
      <ul className="space-y-2">
        {view.locations.map((location) => (
          <li key={location.locationId} data-testid="voice-location">
            <LocationEditor view={view} location={location} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function LocationEditor({ view, location }: { view: VoiceRoutingView; location: VoiceLocationDefaults }) {
  const { t } = useTranslation();
  const outcome = useOutcome();
  const [draft, setDraft] = useState<VoiceOverrides>(location.overrides);
  const save = useMutation({
    mutationFn: (body: VoiceOverrides) => companyApi.saveLocationVoiceDefaults(location.locationId, body),
    onSuccess: (data) => outcome.applied(data, t('voiceRouting.locationSaved', { name: location.name })),
    onError: outcome.failed,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(draft);
  };
  return (
    <form onSubmit={submit} noValidate className="rounded border border-[color:var(--color-border)] p-3">
      <h4 className="mb-2 font-semibold">{location.name}</h4>
      <OutcomeLine status={outcome.status} />
      <OverridesFields value={draft} effective={view.companyDefaults} onChange={setDraft} />
      <div className="mt-3">
        <Button type="submit" busy={save.isPending} disabled={!view.available}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// The call log
// ---------------------------------------------------------------------------

function outcomeOf(t: (key: string, options?: Record<string, unknown>) => string, call: VoiceCallLogEntry): string {
  if (call.answeredByName) return t('voiceRouting.log.answeredBy', { name: call.answeredByName });
  if (call.answered) return t('voiceRouting.log.answered');
  if (call.voicemail) return t('voiceRouting.log.voicemail');
  if (call.status === 'in-progress' || call.status === 'ringing' || call.status === 'queued')
    return t('voiceRouting.log.inProgress');
  return t('voiceRouting.log.missed');
}

function RecentCalls({ view }: { view: VoiceRoutingView }) {
  const { t, i18n } = useTranslation();
  const query = useQuery({ queryKey: ['voice-call-log'], queryFn: () => companyApi.voiceCallLog({ limit: 25 }) });
  const calls = query.data?.calls ?? [];
  const numberLabel = (call: VoiceCallLogEntry) => {
    const number = view.numbers.find((candidate) => candidate.id === call.numberId);
    return number?.label ?? formatPhoneForDisplay(call.to);
  };
  return (
    <div>
      <h3 className="mb-1 font-semibold">{t('voiceRouting.log.heading')}</h3>
      {query.isLoading ? <LoadingBlock label={t('voiceRouting.loading')} /> : null}
      {query.isError ? <Alert tone="warning">{t('voiceRouting.log.error')}</Alert> : null}
      {!query.isLoading && calls.length === 0 ? <p className="text-sm">{t('voiceRouting.log.empty')}</p> : null}
      {calls.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th className="pr-2">{t('voiceRouting.log.when')}</th>
              <th className="pr-2">{t('voiceRouting.log.number')}</th>
              <th className="pr-2">{t('voiceRouting.log.from')}</th>
              <th className="pr-2">{t('voiceRouting.log.outcome')}</th>
              <th>{t('voiceRouting.log.duration')}</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((call) => (
              <tr key={call.id} data-testid="voice-call">
                <td className="pr-2">{new Date(call.startedAt).toLocaleString(i18n.language)}</td>
                <td className="pr-2">
                  {numberLabel(call)}
                  {call.locationId ? ` · ${nameOf(view, 'location', call.locationId)}` : ''}
                </td>
                <td className="pr-2">
                  {call.from === 'anonymous' ? t('voiceRouting.log.withheld') : formatPhoneForDisplay(call.from)}
                </td>
                <td className="pr-2">{outcomeOf(t, call)}</td>
                <td>
                  {call.durationSeconds === null ? '' : t('voiceSettings.seconds', { seconds: call.durationSeconds })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
